
"use client";

import React, { createContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { 
    initializeApp, 
    getApps, 
    getApp, 
    FirebaseApp 
} from 'firebase/app';
import { 
    getAuth, 
    onAuthStateChanged, 
    Auth, 
    User as FirebaseUser,
    signOut,
} from 'firebase/auth';
import { 
    getFirestore, 
    Firestore,
    doc,
    getDoc,
    setDoc,
    serverTimestamp
} from 'firebase/firestore';
import { getStorage, FirebaseStorage } from 'firebase/storage';
import { Toaster } from '@/components/ui/toaster';
import { firebaseClientConfig } from '@/lib/firebase-client-config';
import { FirebaseContext, FirebaseContextType } from '@/firebase';
import type { UserProfile } from '@/types';

function FirebaseClientProvider({ children }: { children: ReactNode }) {
    const router = useRouter();
    
    // Firebase service instances
    const [app, setApp] = useState<FirebaseApp | null>(null);
    const [auth, setAuth] = useState<Auth | null>(null);
    const [firestore, setFirestore] = useState<Firestore | null>(null);
    const [storage, setStorage] = useState<FirebaseStorage | null>(null);
    
    // User state
    const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
    const [user, setUser] = useState<UserProfile | null>(null); // App user profile
    const [isUserLoading, setIsUserLoading] = useState(true);

    // Initialize Firebase app and services ONCE.
    useEffect(() => {
        const appInstance = !getApps().length ? initializeApp(firebaseClientConfig) : getApp();
        const authInstance = getAuth(appInstance);
        const firestoreInstance = getFirestore(appInstance);
        const storageInstance = getStorage(appInstance);

        setApp(appInstance);
        setAuth(authInstance);
        setFirestore(firestoreInstance);
        setStorage(storageInstance);
        
        // Listener for Firebase Auth state changes
        const unsubscribe = onAuthStateChanged(authInstance, async (fbUser) => {
            setFirebaseUser(fbUser);
            if (fbUser) {
                // If a firebase user is detected, fetch their profile from Firestore
                const userRef = doc(firestoreInstance, "users", fbUser.uid);
                const docSnap = await getDoc(userRef);
                if (docSnap.exists()) {
                    setUser({ id: docSnap.id, ...docSnap.data() } as UserProfile);
                } else {
                    // If no profile exists, create one with default inactive state
                    const newUserProfile: Omit<UserProfile, 'id' | 'createdAt' | 'updatedAt'> = {
                        email: fbUser.email || "",
                        displayName: fbUser.displayName || "Sin Nombre",
                        photoURL: fbUser.photoURL || "",
                        role: "Inactivo",
                        isActive: false,
                    };
                    await setDoc(userRef, { ...newUserProfile, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
                    setUser({ id: fbUser.uid, ...newUserProfile } as UserProfile);
                }
            } else {
                // No firebase user, so clear our app user profile
                setUser(null);
            }
            // Finished loading auth state
            setIsUserLoading(false);
        });

        return () => unsubscribe();
    }, []);


    const logout = useCallback(async () => {
        if (!auth) return;
        await signOut(auth);
        // The onAuthStateChanged listener above will handle clearing the user state
        router.push('/login');
    }, [auth, router]);

    const value: FirebaseContextType = {
        app, auth, firestore, storage,
        user, firebaseUser, isUserLoading,
        logout,
        // The actual login logic is handled in the login page
        // These are now just placeholders to satisfy the type
        loginWithGoogle: async () => {},
        loginWithRole: async () => {},
    };
    
    return <FirebaseContext.Provider value={value}>{children}</FirebaseContext.Provider>;
}

export function Providers({ children }: { children: ReactNode }) {
    return (
        <FirebaseClientProvider>
            {children}
            <Toaster />
        </FirebaseClientProvider>
    );
}

